import {
  trackCoachmarkCompleted,
  trackCoachmarkShown,
  trackCoachmarkSkipped,
} from "@/utils/analytics";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOnboardingStatus } from "../status";
import {
  allAnchors,
  diffPresence,
  fired,
  resetScope,
  survey,
  triggerAnchors,
  type Edge,
  type Presence,
} from "./presence";
import { closeCoachRequest, type CoachRequest } from "./request";
import { seenPatch } from "./seen";
import { firstVisible, observe } from "./targets";
import {
  BEAT_HOLD_MS,
  BEAT_SETTLE_MS,
  CHAIN_DELAY_MS,
  TARGET_WAIT_MS,
  type Anchor,
  type Card,
  type Moment,
  type ShellFacts,
} from "./types";

/**
 * Arbitration: which moment and card are up, whether a beat is running, what
 * waits and what has paused. A pure reducer over ticks — every timer is a
 * deadline against the tick's `now`, so the hook below only has to call it
 * ten times a second and carry out the effects it returns.
 */
export type Phase =
  /** The ring rests on the beat's target before the simulated click. */
  | { kind: "beat-hold"; target: Anchor; until: number }
  /** The click ran; the shell is settling before the card appears. */
  | { kind: "beat-settle"; until: number }
  /** The card's target is not on screen yet. */
  | { kind: "waiting"; since: number }
  /** The card is up — hidden, not gone, while its target is occluded. */
  | { kind: "showing" }
  /** The card's target vanished while it was up. */
  | { kind: "gone"; since: number };

export interface Active {
  moment: string;
  card: number;
  phase: Phase;
  /** The last card index this run showed, for the completion event. */
  lastShown: number | null;
}

export interface RunnerState {
  active: Active | null;
  /** Where each paused moment was left, for the session. */
  progress: Readonly<Record<string, number>>;
  /** A Done just happened; the next moment in the chain opens at `at`. */
  chain: { from: string; at: number } | null;
}

export const INITIAL: RunnerState = { active: null, progress: {}, chain: null };

/** What the DOM holds this tick: on screen at all, and the part a modal covers. */
export interface Sight {
  visible: ReadonlySet<Anchor>;
  occluded: ReadonlySet<Anchor>;
  /** Toggles that say they are shut. */
  collapsed: ReadonlySet<Anchor>;
  facts: ShellFacts;
}

/** The beat's own predicate, beyond the card's target being missing. */
const beatHolds = (card: Card, { visible, collapsed }: Sight): boolean =>
  !!card.before &&
  visible.has(card.before.target) &&
  (card.before.when !== "target-collapsed" ||
    collapsed.has(card.before.target));

export interface TickInput extends Sight {
  now: number;
  edges: readonly Edge[];
  seen: ReadonlySet<string>;
  /** A moment asked for by name (request.ts): opened ahead of any trigger. */
  request?: string | null;
}

export interface CardEvent {
  moment: string;
  card: string;
  step: number;
  count: number;
}

export type Effect =
  | { type: "click"; target: Anchor }
  | ({ type: "shown" | "completed" | "skipped" } & CardEvent)
  | { type: "seen"; moment: string }
  /** The reader left mid-walk: the moment is spent, no card event fires. */
  | { type: "left"; moment: string; card: number };

export interface Step {
  state: RunnerState;
  effects: Effect[];
}

export type Verb = "next" | "skip" | "done" | "leave";

const momentOf = (moments: readonly Moment[], key: string): Moment | null =>
  moments.find((m) => m.key === key) ?? null;

const list = (v: string | string[] | undefined): string[] =>
  ([] as string[]).concat(v ?? []);

/** Unseen, its exceptions false, and everything it requires already seen. */
const eligible = (
  m: Moment,
  seen: ReadonlySet<string>,
  facts: ShellFacts,
): boolean =>
  !seen.has(m.key) &&
  !m.unless?.(facts) &&
  list(m.requires).every((k) => seen.has(k));

/** The cards that apply under these facts, in order. */
export const liveCards = (m: Moment, facts: ShellFacts): Card[] =>
  m.cards.filter((c) => c.applies?.(facts) !== false);

/**
 * A card's place among the cards that apply: what "n of m", Done and the
 * analytics report. The card itself always counts, should its facts turn
 * while it is up.
 */
export function position(
  m: Moment,
  index: number,
  facts: ShellFacts,
): { step: number; count: number; last: boolean } {
  const card = m.cards[index];
  const live = m.cards.filter(
    (c) => c === card || c.applies?.(facts) !== false,
  );
  const at = live.indexOf(card);
  return { step: at + 1, count: live.length, last: at === live.length - 1 };
}

const event = (m: Moment, index: number, facts: ShellFacts): CardEvent => {
  const { step, count } = position(m, index, facts);
  return { moment: m.key, card: m.cards[index].key, step, count };
};

const withActive = (state: RunnerState, active: Active | null): RunnerState =>
  active === state.active ? state : { ...state, active };

const forget = (
  progress: Readonly<Record<string, number>>,
  key: string,
): Readonly<Record<string, number>> => {
  if (!(key in progress)) return progress;
  const next = { ...progress };
  delete next[key];
  return next;
};

/** The card is up; Shown fires once its target is uncovered, and once per run. */
function show(
  a: Active,
  m: Moment,
  { occluded, facts }: Sight,
  effects: Effect[],
): Active {
  if (a.lastShown === a.card || occluded.has(m.cards[a.card].target))
    return a.phase.kind === "showing"
      ? a
      : { ...a, phase: { kind: "showing" } };
  effects.push({ type: "shown", ...event(m, a.card, facts) });
  return { ...a, phase: { kind: "showing" }, lastShown: a.card };
}

/**
 * Open a card. One that does not apply is passed over. Target on screen:
 * the card shows. Missing target with a beat whose own target is on screen:
 * the beat runs first. Otherwise the card waits for its target.
 */
function enter(
  state: RunnerState,
  m: Moment,
  index: number,
  now: number,
  sight: Sight,
  lastShown: number | null,
  effects: Effect[],
): RunnerState {
  const { visible, facts } = sight;
  while (index < m.cards.length && m.cards[index].applies?.(facts) === false)
    index++;
  if (index === m.cards.length)
    return finish(state, m, "completed", lastShown, now, facts, effects);
  const card = m.cards[index];
  const progress = { ...state.progress, [m.key]: index };
  let active: Active;
  if (visible.has(card.target)) {
    active = show(
      { moment: m.key, card: index, phase: { kind: "showing" }, lastShown },
      m,
      sight,
      effects,
    );
  } else if (card.before && beatHolds(card, sight)) {
    active = {
      moment: m.key,
      card: index,
      phase: {
        kind: "beat-hold",
        target: card.before.target,
        until: now + (card.before.hold ?? BEAT_HOLD_MS),
      },
      lastShown,
    };
  } else {
    active = {
      moment: m.key,
      card: index,
      phase: { kind: "waiting", since: now },
      lastShown,
    };
  }
  return { ...state, progress, active };
}

/** The moment is over: stamp it, and chain when it completed rather than was skipped. */
function finish(
  state: RunnerState,
  m: Moment,
  how: "completed" | "skipped",
  at: number | null,
  now: number,
  facts: ShellFacts,
  effects: Effect[],
): RunnerState {
  if (at !== null) effects.push({ type: how, ...event(m, at, facts) });
  effects.push({ type: "seen", moment: m.key });
  return {
    active: null,
    progress: forget(state.progress, m.key),
    chain:
      how === "completed" ? { from: m.key, at: now + CHAIN_DELAY_MS } : null,
  };
}

/** Next card, or completion when there is none left. */
function advance(
  state: RunnerState,
  m: Moment,
  a: Active,
  now: number,
  sight: Sight,
  effects: Effect[],
): RunnerState {
  const next = a.card + 1;
  if (next < m.cards.length)
    return enter(state, m, next, now, sight, a.lastShown, effects);
  return finish(state, m, "completed", a.lastShown, now, sight.facts, effects);
}

function tickActive(
  state: RunnerState,
  a: Active,
  m: Moment,
  c: Card,
  input: TickInput,
  effects: Effect[],
): RunnerState {
  const { now, visible, occluded } = input;
  const here = visible.has(c.target);
  switch (a.phase.kind) {
    case "beat-hold":
      // The viewer opened the way themselves during the hold: the click
      // would close it again. The same toggle serves both states.
      if (here) return withActive(state, show(a, m, input, effects));
      // The toggle opened during the hold (a slow tree, or the viewer): a
      // click would shut it again; wait for what it holds instead.
      if (!beatHolds(c, input))
        return withActive(state, {
          ...a,
          phase: { kind: "waiting", since: now },
        });
      // A click behind an open menu would land on nothing the viewer sees.
      if (now < a.phase.until || occluded.has(a.phase.target)) return state;
      effects.push({ type: "click", target: a.phase.target });
      return withActive(state, {
        ...a,
        phase: {
          kind: "beat-settle",
          until: now + (c.before?.settle ?? BEAT_SETTLE_MS),
        },
      });
    case "beat-settle":
      if (now < a.phase.until) return state;
      return withActive(state, {
        ...a,
        phase: { kind: "waiting", since: now },
      });
    case "waiting":
      if (here) return withActive(state, show(a, m, input, effects));
      if (now - a.phase.since < (c.wait ?? TARGET_WAIT_MS)) return state;
      // Never shown pointing at nothing: the card is skipped.
      return advance(state, m, a, now, input, effects);
    case "showing":
      // Occluded is still here: no clock runs, nothing fires, the card waits
      // under the modal for it to lift.
      if (here) return withActive(state, show(a, m, input, effects));
      return withActive(state, { ...a, phase: { kind: "gone", since: now } });
    case "gone": {
      if (here) return withActive(state, show(a, m, input, effects));
      const next = m.cards[a.card + 1];
      if (c.advance === "target-gone") {
        // The viewer did what the last card asked: the moment is done.
        if (!next)
          return finish(
            state,
            m,
            "completed",
            a.card,
            now,
            input.facts,
            effects,
          );
        if (visible.has(next.target))
          return advance(state, m, a, now, input, effects);
      }
      if (now - a.phase.since < TARGET_WAIT_MS) return state;
      // Pause, not seen: the card index stays in `progress`.
      return withActive(state, null);
    }
  }
}

export function tick(
  state: RunnerState,
  input: TickInput,
  moments: readonly Moment[],
): Step {
  const effects: Effect[] = [];
  const { now, visible, edges, seen, facts } = input;
  const sight: Sight = input;

  if (state.active) {
    const m = momentOf(moments, state.active.moment);
    const c = m?.cards[state.active.card];
    if (!m || !c) return { state: withActive(state, null), effects };
    return {
      state: tickActive(state, state.active, m, c, input, effects),
      effects,
    };
  }

  if (state.chain && now >= state.chain.at) {
    const from = state.chain.from;
    const next = moments.find(
      (m) =>
        list(m.after).includes(from) &&
        eligible(m, seen, facts) &&
        visible.has(m.cards[0].target),
    );
    const cleared = { ...state, chain: null };
    if (next) {
      return {
        state: enter(
          cleared,
          next,
          state.progress[next.key] ?? 0,
          now,
          sight,
          null,
          effects,
        ),
        effects,
      };
    }
    state = cleared;
  }

  // A moment asked for by name opens ahead of the triggers.
  if (input.request) {
    const m = momentOf(moments, input.request);
    if (m && !seen.has(m.key))
      return {
        state: enter(
          state,
          m,
          state.progress[m.key] ?? 0,
          now,
          sight,
          null,
          effects,
        ),
        effects,
      };
  }

  // Registry order wins; an edge not taken this tick is gone. A paused
  // moment also reopens when the card it was left on gets its target back.
  for (const m of moments) {
    if (!eligible(m, seen, facts)) continue;
    const paused = state.progress[m.key];
    const back =
      paused !== undefined &&
      fired({ kind: "appears", target: m.cards[paused].target }, edges);
    if (!back && !(m.when && fired(m.when, edges))) continue;
    return {
      state: enter(
        state,
        m,
        state.progress[m.key] ?? 0,
        now,
        sight,
        null,
        effects,
      ),
      effects,
    };
  }
  return { state, effects };
}

export function act(
  state: RunnerState,
  verb: Verb,
  now: number,
  sight: Sight,
  moments: readonly Moment[],
): Step {
  const effects: Effect[] = [];
  const a = state.active;
  const m = a ? momentOf(moments, a.moment) : null;
  if (!a || !m) return { state, effects };
  switch (verb) {
    case "next":
      return { state: advance(state, m, a, now, sight, effects), effects };
    case "done":
      return {
        state: finish(state, m, "completed", a.card, now, sight.facts, effects),
        effects,
      };
    case "skip":
      return {
        state: finish(state, m, "skipped", a.card, now, sight.facts, effects),
        effects,
      };
    case "leave":
      effects.push({ type: "left", moment: m.key, card: a.card });
      return {
        state: finish(state, m, "skipped", null, now, sight.facts, effects),
        effects,
      };
  }
}

/** The layer stood down (narrow, a dialog's stage, the flag): pause, keep the session's progress. */
export function deactivate(state: RunnerState): RunnerState {
  return state.active || state.chain
    ? { ...state, active: null, chain: null }
    : state;
}

export interface RunnerHandle {
  active: Active | null;
  next: () => void;
  skip: () => void;
  done: () => void;
}

/**
 * Drives the reducer: one measuring loop over every anchor the registry
 * names (the same cadence as the ring), presence edges for the triggers,
 * the simulated clicks, the seen stamps and the analytics seam.
 */
export function useCoachRunner({
  enabled,
  moments,
  pathname,
  seen,
  facts,
  request = null,
}: {
  enabled: boolean;
  moments: readonly Moment[];
  pathname: string;
  seen: ReadonlySet<string>;
  facts: ShellFacts;
  request?: CoachRequest | null;
}): RunnerHandle {
  const status = useOnboardingStatus();
  const statusRef = useRef(status);
  statusRef.current = status;
  const stateRef = useRef<RunnerState>(INITIAL);
  const [active, setActive] = useState<Active | null>(null);
  const presenceRef = useRef<Presence>({});
  const seenRef = useRef(seen);
  seenRef.current = seen;
  const factsRef = useRef(facts);
  factsRef.current = facts;
  const requestRef = useRef(request);
  requestRef.current = request;

  const apply = useCallback(
    ({ state, effects }: Step) => {
      stateRef.current = state;
      setActive(state.active);
      for (const e of effects) {
        switch (e.type) {
          case "click":
            firstVisible(e.target)?.click();
            break;
          case "seen": {
            const m = momentOf(moments, e.moment);
            if (m) void statusRef.current.patch(seenPatch(m));
            break;
          }
          case "left":
            if (requestRef.current?.moment === e.moment)
              closeCoachRequest("left", e.card);
            break;
          case "shown":
          case "completed":
          case "skipped": {
            const { moment, card, step, count } = e;
            const track = {
              shown: trackCoachmarkShown,
              completed: trackCoachmarkCompleted,
              skipped: trackCoachmarkSkipped,
            }[e.type];
            track({ moment, card, step, count });
            if (e.type === "shown" && requestRef.current?.moment === moment)
              requestRef.current.onCard?.(step - 1, card);
            if (e.type !== "shown" && requestRef.current?.moment === moment)
              closeCoachRequest(
                e.type === "completed" ? "done" : "skip",
                step - 1,
              );
            break;
          }
        }
      }
    },
    [moments],
  );

  // Entering a route forgets its anchors, so a node that persisted appears again.
  const lastPath = useRef(pathname);
  if (lastPath.current !== pathname) {
    lastPath.current = pathname;
    presenceRef.current = resetScope(
      presenceRef.current,
      triggerAnchors(moments)
        .filter((t) => t.scope === "route")
        .map((t) => t.target),
    );
  }
  useEffect(() => {
    if (!enabled) return undefined;
    const anchors = allAnchors(moments);
    // Baseline absent: whatever is on screen at activation has appeared.
    presenceRef.current = {};
    const stop = observe(() => {
      // A requested moment belongs to the surface it was asked for: on any
      // other route the walk is over, the cards spent, the asker told.
      const asked = requestRef.current;
      if (asked && asked.pathname !== pathname) {
        const a = stateRef.current.active;
        if (a?.moment === asked.moment) {
          const { visible, occluded, collapsed } = survey(anchors);
          const sight = {
            visible,
            occluded,
            collapsed,
            facts: factsRef.current,
          };
          apply(act(stateRef.current, "leave", Date.now(), sight, moments));
        } else {
          const m = momentOf(moments, asked.moment);
          if (m && !seenRef.current.has(m.key))
            void statusRef.current.patch(seenPatch(m));
          closeCoachRequest(
            "left",
            stateRef.current.progress[asked.moment] ?? 0,
          );
        }
        return;
      }
      const { now, visible, occluded, collapsed } = survey(anchors);
      // Every anchor, not only triggers: a paused card's own target
      // appearing is how its moment comes back.
      const edges = diffPresence(presenceRef.current, now, anchors);
      presenceRef.current = now;
      const step = tick(
        stateRef.current,
        {
          now: Date.now(),
          visible,
          occluded,
          collapsed,
          edges,
          seen: seenRef.current,
          facts: factsRef.current,
          request: requestRef.current?.moment ?? null,
        },
        moments,
      );
      if (step.state !== stateRef.current || step.effects.length) apply(step);
    });
    return () => {
      stop();
      apply({ state: deactivate(stateRef.current), effects: [] });
    };
  }, [enabled, moments, apply, pathname]);

  const verb = useCallback(
    (v: Verb) => {
      if (!stateRef.current.active) return;
      const { visible, occluded, collapsed } = survey(allAnchors(moments));
      const sight = { visible, occluded, collapsed, facts: factsRef.current };
      apply(act(stateRef.current, v, Date.now(), sight, moments));
    },
    [moments, apply],
  );
  const next = useCallback(() => verb("next"), [verb]);
  const skip = useCallback(() => verb("skip"), [verb]);
  const done = useCallback(() => verb("done"), [verb]);

  return { active, next, skip, done };
}
